import { get } from 'lodash'

/**
 * Локальное состояние поля.
 *
 * @typedef {object} SelectState
 * @property {boolean} _active Признак активности поля.
 * @property {boolean} _loading Признак загрузки элементов.
 * @property {boolean} _focused Признак фокуса на поле.
 * @property {string|undefined} _error Текст текущей ошибки.
 * @property {boolean} _errorOnRequired Признак ошибки обязательного
 * поля с пустым выбором.
 * @property {boolean} _requiredChecked Признак выполненной проверки
 * обязательного выбора.
 */

/**
 * Состояние поля выпадающего списка: активность, загрузка, фокус и
 * ошибка, а также применение обязательного выбора.
 */
export const state = {
  props: {
    /**
     * Признак обязательного поля. Рядом с подписью выводится знак
     * `*`, последнее выбранное значение нельзя снять, а полная
     * очистка выбора не выполняется.
     *
     * @type {boolean}
     * @default false
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Признак выбора первого доступного элемента после
     * монтирования при required и пустом выборе.
     *
     * @type {boolean}
     * @default true
     */
    setRequiredOnMounted: {
      type: Boolean,
      default: true
    },

    /**
     * Текст ошибки. Выводится слева в строке пояснений вместо hint и
     * добавляет корневому элементу модификатор ошибки.
     *
     * @type {string}
     */
    error: String,

    /**
     * Признак активности поля. Задает модификатор класса
     * корневого элемента наравне с раскрытым списком.
     *
     * @type {boolean}
     * @default false
     */
    active: {
      type: Boolean,
      default: false
    },
    /**
     * Признак фокуса на поле. Включает подсветку подписи, границы
     * и значков по краям поля.
     *
     * @type {boolean}
     * @default false
     */
    focused: {
      type: Boolean,
      default: false
    },
    /**
     * Признак загрузки. Выводит индикатор загрузки и модификатор
     * класса корневого элемента.
     *
     * @type {boolean}
     * @default false
     */
    loading: {
      type: Boolean,
      default: false
    }
  },

  /**
   * Локальное состояние поля. Начальные значения берутся из
   * одноименных свойств.
   *
   * @returns {SelectState} Локальное состояние.
   */
  data() {
    return {
      _active: this.active,
      _loading: this.loading,
      _focused: this.focused,
      _error: this.error,
      _errorOnRequired: false,
      _requiredChecked: false
    }
  },

  computed: {
    /**
     * Отдает признак активности поля.
     * Поле активно, если задан признак активности или список
     * раскрыт.
     *
     * @returns {boolean} Признак активности поля.
     */
    isActive() {
      return this._data._active || this._data._showDropdown
    },
    /**
     * Отдает признак загрузки.
     *
     * @returns {boolean} Признак загрузки.
     */
    isLoading() {
      return this._data._loading
    },
    /**
     * Отдает признак фокуса на поле.
     *
     * @returns {boolean} Признак фокуса на поле.
     */
    isFocused() {
      return this._data._focused
    },
    /**
     * Отдает признак ошибки.
     * Ошибка есть, если задан признак ошибки обязательного поля или
     * текст ошибки.
     *
     * @returns {boolean} Признак ошибки.
     */
    isError() {
      return this._data._errorOnRequired || !!this._data._error
    }
  },

  watch: {
    /**
     * Отслеживает свойство active и обновляет признак активности в
     * локальном состоянии.
     *
     * @param {boolean} value Новое значение свойства.
     */
    active(value) {
      this._data._active = value
    },
    /**
     * Отслеживает свойство loading и обновляет признак загрузки в
     * локальном состоянии.
     *
     * @param {boolean} value Новое значение свойства.
     */
    loading(value) {
      this._data._loading = value
    },
    /**
     * Отслеживает свойство focused и обновляет признак фокуса в
     * локальном состоянии.
     *
     * @param {boolean} value Новое значение свойства.
     */
    focused(value) {
      this._data._focused = value
    },
    /**
     * Отслеживает свойство error и обновляет текст текущей ошибки
     * в локальном состоянии.
     *
     * @param {string} value Новое значение свойства.
     */
    error(value) {
      this._data._error = value
    },
    /**
     * Отслеживает признак загрузки и пересчитывает признак ошибки
     * обязательного поля. Пока элементы загружаются из сервиса,
     * признак снят, а после ответа проверка выполняется заново.
     */
    isLoading() {
      this._updateErrorOnRequired()
    },
    /**
     * Отслеживает признак пустого выбора и пересчитывает признак
     * ошибки обязательного поля. Значение приходит после
     * автовыбора или ответа сервиса, когда проверка обязательного
     * выбора уже выполнена.
     */
    proxyIsEmpty() {
      this._updateErrorOnRequired()
    }
  },

  methods: {
    /**
     * Выбирает первый доступный элемент для обязательного поля.
     * Если нет setRequiredOnMounted, или нет required, или выбор не
     * пуст, или список элементов пуст, ничего не происходит. Иначе
     * в выбор передается первый незаблокированный элемент; если
     * такого элемента нет, выбор не меняется.
     */
    _setRequired() {
      if (this.setRequiredOnMounted) {
        if (this.required) {
          if (this.proxyIsEmpty) {
            if (this.displayItems.length) {
              const item = this.displayItems.find(item => {
                return item._disabled !== true
              })
              this.proxy = [ get(item, this.$_itemValue, item) ]
            }
          }
        }
      }
    },

    /**
     * Проверяет обязательный выбор при потере фокуса.
     * Проверка выполняется после перерисовки, потому что выбор
     * приходит из свойства value и обновляется только после того,
     * как событие input дойдет наружу и вернется новым значением.
     * Дальше признак ошибки пересчитывается при каждом изменении
     * выбора и признака загрузки.
     */
    _setErrorOnRequired() {
      this.$nextTick(() => {
        this._data._requiredChecked = true
        this._updateErrorOnRequired()
      })
    },

    /**
     * Пересчитывает признак ошибки обязательного поля.
     * Признак выставляется, если проверка обязательного выбора уже
     * выполнялась, поле обязательное и выбор пуст. Пока элементы
     * загружаются из сервиса, признак снят, чтобы не подсвечивать
     * поле до ответа.
     */
    _updateErrorOnRequired() {
      const isEmptyRequired = this.required && this.proxyIsEmpty
      const isLoadingItems = !!this.service && this.isLoading

      this._data._errorOnRequired = this._data._requiredChecked && isEmptyRequired && !isLoadingItems
    },

    /**
     * Сбрасывает признак выполненной проверки обязательного выбора,
     * признак ошибки и текст текущей ошибки.
     */
    _clearCurrentError() {
      this._data._requiredChecked = false
      this._data._errorOnRequired = false
      this._data._error = undefined
    }
  }
}
